import { useRef, useState, type ComponentProps, type ReactNode, type RefObject } from 'react';
import { ArrowLeftIcon, CaretDownIcon, CheckIcon, UsersIcon } from '@phosphor-icons/react';
import { AvatarGroup, AvatarGroupCount } from '@voll-intelligence/ui-kit/ui/avatar';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Checkbox } from '@voll-intelligence/ui-kit/ui/checkbox';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@voll-intelligence/ui-kit/ui/command';
import { Popover, PopoverContent } from '@voll-intelligence/ui-kit/ui/popover';
import { FIELD_TRIGGER_GLYPH_CLASS, FieldTrigger } from '../field_trigger.js';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { LoadMoreSentinel } from '../load_more_sentinel.js';
import { NARROW_STAGE_QUERY } from '../narrow_stage.js';
import { PageFieldSheet } from '../page_field_sheet.js';
import { TravelerPickerStageContext } from './traveler_picker_stage.js';
import { returnFocusUnlessMoved } from '../return_focus.js';
import { PersonAvatar, PersonRow, type PersonItem } from './person_row.js';
import { shortPersonName } from './person_name.js';

/*
 * O seletor de pessoa das três modais (aéreo, hotel, rent a car) sobre o DS:
 * `Command` (busca + lista + teclado), `Checkbox` no multi, marca no single,
 * `Popover` no palco largo e `PageFieldSheet` no estreito. O que muda por
 * modal entra por prop: `mode`, `pinned` ("Você"), `disabled` com motivo (o
 * hóspede já alocado noutro quarto), `min`/`max` (o 1..9 do aéreo), `header`
 * (o contexto do quarto) e o rodapé do campo (a política do aéreo).
 *
 * Invariantes herdadas do `TravelersField` (genui-kit) e do `GuestPicker`
 * (hotel), que os consumidores contam com:
 *  - "Você" é uma linha como as outras: pode ser desmarcada, desde que reste ≥ `min`.
 *  - O último escolhido não se desmarca (`min`), o próximo além do teto não se
 *    marca (`max`) — a linha fica visível com o motivo, em vez de ignorar o clique.
 *  - Sem `searchable` (host sem port de busca) não há campo de busca e a lista é
 *    quem já está no valor — o degrade do card legado/preview.
 *  - Carregando a 1ª página: esqueleto de linhas; carregando a próxima: spinner
 *    no pé e a lista fica. Erro: `role="alert"`. Vazio: mensagem.
 *  - Escape e clique fora fecham e devolvem o foco ao gatilho (Base UI); um
 *    popup aberto de dentro do popover é uma camada acima e não conta como "fora".
 */

export type TravelerSearchState = 'loading' | 'ready' | 'error';

export type TravelerPickerCopy = {
  readonly label: string;
  readonly searchPlaceholder: string;
  readonly empty: string;
  readonly error: string;
  readonly loadMore: string;
  /** A linha fixa da própria pessoa. */
  readonly you: string;
  /** Legenda da linha fixa quando ela tem nome: "Felipe Borges · viajante". */
  readonly youRole: string;
  /** Cabeçalho do grupo dos já escolhidos, na lista sem busca digitada. */
  readonly selectedGroup: string;
  /** Legenda da linha bloqueada por teto ("Máximo de 9 viajantes"). */
  readonly maxReached: string;
  /** Legenda do último escolhido, que não se desmarca ("Ao menos 1 viajante"). */
  readonly minReached: string;
};

export type TravelerPickerProps = {
  readonly mode: 'multi' | 'single';
  readonly items: readonly PersonItem[];
  /** As pessoas escolhidas, com nome — aparecem no topo mesmo fora da página atual. */
  readonly selected: readonly PersonItem[];
  /** A pessoa fixa no topo (o solicitante), fora da busca e da paginação. */
  readonly pinned?: PersonItem;
  /** id → motivo: a linha fica visível, apagada e com o motivo na legenda. */
  readonly disabled?: Readonly<Record<string, string>>;
  readonly min?: number;
  readonly max?: number;
  /** Falso quando o host não tem busca: some o campo e a lista é o próprio valor. */
  readonly searchable?: boolean;
  readonly query: string;
  readonly searchState: TravelerSearchState;
  readonly hasMore: boolean;
  readonly copy: TravelerPickerCopy;
  readonly locale?: string;
  /** Cabeçalho acima da busca — o "Quarto 1 · acompanhante" do hotel. */
  readonly header?: ReactNode;
  /** Largura do pai (folha) em vez dos 19rem do popover. Ausente ⇒ segue o
   *  palco: estreito preenche, largo mede 19rem. */
  readonly fill?: boolean;
  readonly onQueryChange: (query: string) => void;
  readonly onLoadMore?: () => void;
  /** Multi: alterna. Single: escolhe (quem fecha é o pai). */
  readonly onSelect: (person: PersonItem) => void;
};

/** Entrada das linhas: fade + 4px de baixo, escalonado pela posição NA PÁGINA
 *  recém-chegada — quem já estava não se mexe. `fill-mode-backwards` segura a
 *  linha invisível durante o atraso. */
function enterClass(index: number): { className: string; style: { animationDelay: string } } {
  return {
    className:
      'animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-backwards duration-200 motion-reduce:animate-none',
    style: { animationDelay: `${String(Math.min(index, 8) * 25)}ms` },
  };
}

function Row({
  person,
  selected,
  disabledReason,
  mode,
  displayName,
  description,
  locale,
  enterIndex,
  onSelect,
}: {
  readonly person: PersonItem;
  readonly selected: boolean;
  readonly disabledReason?: string;
  readonly mode: 'multi' | 'single';
  readonly displayName?: string;
  readonly description?: string;
  readonly locale?: string;
  readonly enterIndex?: number;
  readonly onSelect: (person: PersonItem) => void;
}): ReactNode {
  const locked = disabledReason !== undefined;
  const enter = enterIndex === undefined ? undefined : enterClass(enterIndex);
  return (
    <CommandItem
      value={person.id}
      disabled={locked}
      // `aria-selected` é do cmdk (o item ativo do teclado); o que a pessoa
      // ESCOLHEU vai em `aria-checked`, que `option` também suporta.
      aria-checked={selected}
      data-checked={selected ? '' : undefined}
      className={`gap-2.5 px-2 py-1.5 ${enter?.className ?? ''}`}
      style={enter?.style}
      onSelect={() => {
        onSelect(person);
      }}
    >
      <PersonRow
        person={person}
        locale={locale}
        selected={selected}
        {...(displayName ? { displayName } : {})}
        {...(locked ? { description: disabledReason } : description ? { description } : {})}
      />
      {mode === 'multi' ? (
        <Checkbox
          checked={selected}
          disabled={locked}
          tabIndex={-1}
          className="pointer-events-none"
        />
      ) : (
        selected && (
          <CheckIcon
            className="size-4 text-foreground animate-in zoom-in-50 duration-150 motion-reduce:animate-none"
            aria-hidden="true"
          />
        )
      )}
    </CommandItem>
  );
}

/** A 1ª página chegando: quatro linhas-fantasma no lugar de um spinner solto —
 *  um `status` nomeado pela lista, para o leitor de tela saber que ela carrega. */
function SkeletonRows({ label }: { readonly label: string }): ReactNode {
  return (
    <div role="status" aria-label={label} className="grid gap-1 p-1">
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="flex items-center gap-2.5 px-2 py-1.5" aria-hidden="true">
          <Skeleton className="size-6 rounded-full" />
          <div className="grid flex-1 gap-1.5">
            <Skeleton className="h-3 w-[55%]" />
            <Skeleton className="h-2.5 w-[70%]" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** O fim da lista: a sentinela da próxima página, ou o spinner enquanto ela chega. */
function ListTail({
  searchState,
  hasMore,
  copy,
  onLoadMore,
}: {
  readonly searchState: TravelerSearchState;
  readonly hasMore: boolean;
  readonly copy: TravelerPickerCopy;
  readonly onLoadMore?: () => void;
}): ReactNode {
  if (searchState === 'loading') {
    return (
      <div className="flex justify-center py-2 animate-in fade-in-0 duration-150">
        <Spinner aria-label={copy.label} />
      </div>
    );
  }
  if (searchState !== 'ready') return null;
  return (
    <LoadMoreSentinel
      hasMore={hasMore}
      loading={false}
      label={copy.loadMore}
      {...(onLoadMore ? { onLoadMore } : {})}
    />
  );
}

/** Sem busca digitada, no multi, os já escolhidos sobem para um grupo próprio — a
 *  pessoa vê quem vai sem rolar até achar cada checkbox marcado. Digitando, a
 *  lista é o resultado puro. */
function partition(
  p: Pick<TravelerPickerProps, 'mode' | 'items' | 'selected' | 'query'>,
  pinned: PersonItem | undefined,
): { chosen: PersonItem[]; rest: PersonItem[] } {
  const searching = p.query.trim() !== '';
  const chosen =
    p.mode === 'multi' && !searching ? p.selected.filter((person) => person.id !== pinned?.id) : [];
  const chosenIds = new Set(chosen.map((person) => person.id));
  const rest = p.items.filter((person) => person.id !== pinned?.id && !chosenIds.has(person.id));
  return { chosen, rest };
}

/** O que a lista mostra quando a BUSCA não trouxe ninguém: esqueleto na 1ª
 *  carga, alerta no erro, a mensagem de vazio. Não é o `CommandEmpty` do cmdk:
 *  ele só aparece sem NENHUM item, e a linha "Você" e os já escolhidos ficam
 *  na lista mesmo com a busca vazia ou quebrada — o estado é dos resultados,
 *  não da lista inteira. Mesma métrica do `CommandEmpty` do DS. */
function EmptyState({
  searchState,
  copy,
}: {
  readonly searchState: TravelerSearchState;
  readonly copy: TravelerPickerCopy;
}): ReactNode {
  if (searchState === 'loading') return <SkeletonRows label={copy.label} />;
  return (
    <div className="py-6 text-center text-sm text-muted-foreground">
      {searchState === 'error' ? <span role="alert">{copy.error}</span> : copy.empty}
    </div>
  );
}

/** Que legenda trava a linha: o motivo do host, o teto, ou o piso. */
function lockReason(
  id: string,
  isSelected: boolean,
  p: Pick<TravelerPickerProps, 'disabled' | 'min' | 'max' | 'copy'> & {
    readonly selectedCount: number;
  },
): string | undefined {
  const fromHost = p.disabled?.[id];
  if (fromHost !== undefined) return fromHost;
  if (!isSelected && p.max !== undefined && p.selectedCount >= p.max) return p.copy.maxReached;
  if (isSelected && p.min !== undefined && p.selectedCount <= p.min) return p.copy.minReached;
  return undefined;
}

export function TravelerPicker(props: TravelerPickerProps): ReactNode {
  const {
    mode,
    items,
    selected,
    pinned,
    searchable = true,
    query,
    searchState,
    hasMore,
    copy,
    locale,
    header,
    fill: fillProp,
    onQueryChange,
    onLoadMore,
    onSelect,
  } = props;
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  const fill = fillProp ?? narrow;
  const selectedIds = new Set(selected.map((person) => person.id));
  const lock = (person: PersonItem) =>
    lockReason(person.id, selectedIds.has(person.id), { ...props, selectedCount: selected.length });

  // Quem já entrou não anima de novo: só as linhas além do que a lista tinha.
  // Counted during render, on each change of length.
  const [counted, setCounted] = useState({ length: 0, firstNew: 0 });
  if (counted.length !== items.length)
    setCounted({ length: items.length, firstNew: counted.length });
  const firstNew = counted.length === items.length ? counted.firstNew : counted.length;

  const { chosen, rest } = partition(props, pinned);

  const row = (
    person: PersonItem,
    extra?: { displayName?: string; description?: string; enterIndex?: number },
  ) => {
    const reason = lock(person);
    return (
      <Row
        key={person.id}
        person={person}
        selected={selectedIds.has(person.id)}
        mode={mode}
        locale={locale}
        onSelect={onSelect}
        {...(reason ? { disabledReason: reason } : {})}
        {...(extra?.displayName ? { displayName: extra.displayName } : {})}
        {...(extra?.description ? { description: extra.description } : {})}
        {...(extra?.enterIndex !== undefined ? { enterIndex: extra.enterIndex } : {})}
      />
    );
  };

  return (
    <div className={`flex min-h-0 flex-col ${fill ? 'w-full flex-1' : 'w-[19rem]'}`}>
      {header}
      <Command label={copy.label} shouldFilter={false} className="min-h-0 flex-1 rounded-none!">
        {searchable && (
          <CommandInput
            aria-label={copy.label}
            placeholder={copy.searchPlaceholder}
            value={query}
            onValueChange={onQueryChange}
          />
        )}
        <CommandList className={fill ? 'max-h-none flex-1' : 'max-h-80'}>
          {pinned && (
            <>
              <CommandGroup>
                {row(pinned, {
                  displayName: copy.you,
                  description: `${shortPersonName(pinned.name, locale)} · ${copy.youRole}`,
                })}
              </CommandGroup>
              <CommandSeparator alwaysRender />
            </>
          )}
          {chosen.length > 0 && (
            <>
              <CommandGroup heading={copy.selectedGroup}>
                {chosen.map((person) => row(person))}
              </CommandGroup>
              <CommandSeparator alwaysRender />
            </>
          )}
          {items.length === 0 && <EmptyState searchState={searchState} copy={copy} />}
          {rest.length > 0 && (
            <CommandGroup>
              {rest.map((person) => {
                const index = items.indexOf(person);
                return row(
                  person,
                  index >= firstNew ? { enterIndex: index - firstNew } : undefined,
                );
              })}
            </CommandGroup>
          )}
          {items.length > 0 && (
            <ListTail
              searchState={searchState}
              hasMore={hasMore}
              copy={copy}
              {...(onLoadMore ? { onLoadMore } : {})}
            />
          )}
        </CommandList>
      </Command>
    </div>
  );
}

/** O cabeçalho do hotel: seta de voltar + o contexto da vaga. */
export function PickerHeader({
  title,
  backLabel,
  onBack,
}: {
  readonly title: string;
  readonly backLabel: string;
  readonly onBack: () => void;
}): ReactNode {
  return (
    <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
      <Button type="button" variant="ghost" size="icon-xs" aria-label={backLabel} onClick={onBack}>
        <ArrowLeftIcon aria-hidden="true" />
      </Button>
      <span className="text-caption text-muted-foreground">{title}</span>
    </div>
  );
}

/** O `trigger` do `TravelerPickerField` com a `TravelerTrigger`: fora do campo
 *  que o usa para não ser um componente aninhado (o ref chega na chamada). */
export function travelerTrigger(
  props: Omit<ComponentProps<typeof TravelerTrigger>, 'triggerRef'>,
): (ref: RefObject<HTMLButtonElement | null>) => ReactNode {
  return (ref) => <TravelerTrigger triggerRef={ref} {...props} />;
}

/** Os retratos empilhados do gatilho QUEM: até `max`, com "+N" além disso. Um
 *  retrato que entra ou sai anima (zoom). Também é o glifo do QUEM do hotel,
 *  que usa a pílula da própria barra. */
export function TravelerAvatarStack({
  people,
  locale,
  max = 3,
}: {
  readonly people: readonly PersonItem[];
  readonly locale?: string | undefined;
  /** Quantos retratos antes do "+N" — o QUEM do hotel, numa coluna de 192px
   *  ao lado do texto, cabe dois. */
  readonly max?: number;
}): ReactNode {
  const shown = people.slice(0, max);
  const overflow = people.length - shown.length;
  return (
    <AvatarGroup className="-space-x-1.5">
      {shown.map((person) => (
        <span
          key={person.id}
          className="contents *:data-[slot=avatar]:animate-in *:data-[slot=avatar]:zoom-in-50 *:data-[slot=avatar]:duration-200 motion-reduce:*:data-[slot=avatar]:animate-none"
        >
          <PersonAvatar
            name={person.name}
            photoUrl={person.photoUrl}
            size="xs"
            {...(locale ? { locale } : {})}
          />
        </span>
      ))}
      {overflow > 0 && (
        <AvatarGroupCount className="size-5 text-[9px]">+{String(overflow)}</AvatarGroupCount>
      )}
    </AvatarGroup>
  );
}

/** O gatilho do campo QUEM do card do aéreo — a `FieldTrigger` compartilhada
 *  com retratos empilhados + resumo. Um retrato que entra ou sai anima (zoom),
 *  o caret gira ao abrir. */
export function TravelerTrigger({
  triggerRef,
  people,
  summary,
  label,
  expanded,
  disabled = false,
  locale,
  onOpen,
}: {
  readonly triggerRef?: RefObject<HTMLButtonElement | null>;
  readonly people: readonly PersonItem[];
  readonly summary: string;
  readonly label: string;
  readonly expanded: boolean;
  readonly disabled?: boolean;
  readonly locale?: string;
  readonly onOpen?: () => void;
}): ReactNode {
  return (
    <FieldTrigger
      ref={triggerRef}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-label={`${label} · ${summary}`}
      disabled={disabled}
      onClick={onOpen}
    >
      {people.length > 0 ? (
        <TravelerAvatarStack people={people} locale={locale} />
      ) : (
        <UsersIcon className={FIELD_TRIGGER_GLYPH_CLASS} aria-hidden="true" />
      )}
      <span className="text-ui min-w-0 truncate">{summary}</span>
      <CaretDownIcon
        className={`${FIELD_TRIGGER_GLYPH_CLASS} transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
        aria-hidden="true"
      />
    </FieldTrigger>
  );
}

/**
 * O campo inteiro: gatilho + o palco certo. Largo → `Popover` do DS ancorado
 * no gatilho; estreito → folha de tela cheia com a pergunta no cabeçalho e o
 * `footer` pregado no pé.
 */
export function TravelerPickerField({
  open,
  onOpenChange,
  title,
  backLabel,
  onBack,
  layout,
  trigger,
  triggerRef: externalTriggerRef,
  returnFocusTo,
  initialFocus,
  footer,
  children,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly backLabel: string;
  /** Ver `PageFieldSheet.onBack` (só a folha: o popover não tem seta). */
  readonly onBack?: () => void;
  /** Força o palco quando o host tem a própria régua de largura (o card do
   *  aéreo decide a folha pelo palco DELE). Ausente ⇒ `NARROW_STAGE_QUERY`. */
  readonly layout?: 'popover' | 'sheet';
  readonly trigger: (ref: RefObject<HTMLButtonElement | null>) => ReactNode;
  /** O ref do gatilho quando o host também precisa dele (a máquina de painéis
   *  do hotel devolve o foco por ele). Ausente ⇒ um ref próprio. */
  readonly triggerRef?: RefObject<HTMLButtonElement | null>;
  /** Para onde a FOLHA devolve o foco ao fechar. Ausente ⇒ o gatilho. O hotel
   *  passa `returnFocusUnlessMoved`: no preenchimento guiado o foco já foi
   *  para a folha do campo seguinte. */
  readonly returnFocusTo?: ComponentProps<typeof PageFieldSheet>['returnFocusTo'];
  /** Onde o foco pousa ao abrir o POPOVER. Ausente ⇒ o primeiro controle (a
   *  busca da lista). O hotel aponta o próprio painel: o primeiro controle
   *  dele é o ⓘ do quarto, e o foco abriria a dica por cima do gatilho. */
  readonly initialFocus?: RefObject<HTMLElement | null>;
  /** Pregado no pé: na folha, fora do rolo; no popover, sob a lista com um fio. */
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  const narrowStage = useMediaQuery(NARROW_STAGE_QUERY);
  const narrow = layout ? layout === 'sheet' : narrowStage;
  const ownTriggerRef = useRef<HTMLButtonElement>(null);
  const triggerRef = externalTriggerRef ?? ownTriggerRef;
  if (narrow) {
    return (
      <>
        {trigger(triggerRef)}
        <PageFieldSheet
          open={open}
          onClose={() => {
            onOpenChange(false);
          }}
          returnFocusTo={returnFocusTo ?? triggerRef}
          title={title}
          backLabel={backLabel}
          {...(onBack ? { onBack } : {})}
          {...(footer
            ? {
                footer: (
                  <TravelerPickerStageContext.Provider value="sheet">
                    {footer}
                  </TravelerPickerStageContext.Provider>
                ),
              }
            : {})}
        >
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </PageFieldSheet>
      </>
    );
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {/* Sem `PopoverTrigger`: o gatilho é do chamador (a pílula do campo), e o
          popover ancora nele pelo ref — o mesmo `anchor` externo dos pinos do mapa. */}
      {trigger(triggerRef)}
      <PopoverContent
        anchor={triggerRef}
        align="start"
        sideOffset={6}
        className="w-auto gap-0 overflow-hidden p-0"
        /* Sem `PopoverTrigger` o Base UI não sabe para onde devolver o foco: a
           regra é a do `PlaceField`. Clique fora deixa o foco onde a pessoa
           clicou; Escape e o «Pronto» o devolvem ao gatilho — salvo se ele já
           foi para o campo seguinte (o preenchimento guiado do hotel). */
        finalFocus={(closeType) =>
          !['mouse', 'touch', 'pen'].includes(closeType) && returnFocusUnlessMoved(triggerRef)()
        }
        {...(initialFocus ? { initialFocus } : {})}
        aria-label={title}
      >
        {children}
        {footer && <div className="grid gap-2 border-t border-border p-2.5">{footer}</div>}
      </PopoverContent>
    </Popover>
  );
}

/** Resumo do gatilho: um nome só mostra a pessoa; dois ou mais, a contagem. */
export function travelerSummary(
  people: readonly PersonItem[],
  requesterId: string | undefined,
  copy: { readonly you: string; readonly count: (n: number) => string },
  locale?: string,
): string {
  const [only] = people;
  if (people.length === 1 && only) {
    return only.id === requesterId ? copy.you : shortPersonName(only.name, locale);
  }
  return copy.count(people.length);
}
